/**
 * RiDi cart enhancements — reward ladder gift logic, in-cart cross-sell and the
 * optional pre-checkout order enhancement ("bump"). Everything here is driven by
 * theme settings and the data attributes rendered by snippets/ridi-cart-rewards,
 * ridi-cart-crosssell and ridi-cart-bump. Every add goes through Shopify's own
 * /cart/add.js with the Horizon section ids so the drawer and page re-render via
 * the stock CartAddEvent / CartUpdateEvent path — no custom cart rendering.
 */
import { CartAddEvent, CartUpdateEvent, ThemeEvents } from '@theme/events';

const routes = (window.Theme && window.Theme.routes) || {};
const CART_ADD = routes.cart_add_url || '/cart/add.js';
const CART_CHANGE = routes.cart_change_url || '/cart/change.js';
const REWARD_PROP = '_ridi_reward';
const BUMP_PROP = '_ridi_bump';

const sectionIds = () => Array.from(document.querySelectorAll('cart-items-component')).map((c) => c.dataset.sectionId).filter(Boolean);

function announce(name, data = {}, category = 'analytics') {
  document.dispatchEvent(new CustomEvent('ridi:event', { detail: { name, data, category } }));
}

async function readCart() {
  const r = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
  return r.ok ? r.json() : null;
}

async function addLine(items, source) {
  const body = { items, sections: sectionIds().join(','), sections_url: window.location.pathname };
  const r = await fetch(CART_ADD, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
  const data = await r.json();
  if (!r.ok) throw new Error(data.description || data.message || 'cart add failed');
  const cart = await readCart();
  document.dispatchEvent(new CartAddEvent({ ...data, item_count: cart ? cart.item_count : undefined }, String(items[0].id), { source, itemCount: cart ? cart.item_count : undefined, sections: data.sections, productId: items[0].id }));
  return cart;
}

async function changeLine(key, quantity, source) {
  const body = { id: key, quantity, sections: sectionIds().join(','), sections_url: window.location.pathname };
  const r = await fetch(CART_CHANGE, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
  const data = await r.json();
  if (!r.ok) throw new Error(data.description || data.message || 'cart change failed');
  document.dispatchEvent(new CartUpdateEvent({ ...data, source }, 'ridi-cart', { source, itemCount: data.item_count, sections: data.sections }));
  return data;
}

/* ---- Reward ladder: gift add / remove ------------------------------------------------------- */
let rewardBusy = false;
const manuallyRemoved = () => { try { return sessionStorage.getItem('ridi:gift-removed') === '1'; } catch { return false; } };

async function syncRewardGift() {
  const ladder = document.querySelector('[data-ridi-rewards][data-ridi-rewards-enabled="true"]');
  if (!ladder || rewardBusy) return;
  const giftVariant = ladder.dataset.ridiGiftVariant;
  const threshold = Number(ladder.dataset.ridiGiftThreshold || 0);
  if (!giftVariant || !threshold) return;
  rewardBusy = true;
  try {
    const cart = await readCart();
    if (!cart) return;
    const giftLine = cart.items.find((i) => i.properties && i.properties[REWARD_PROP] === 'gift');
    const qualifying = cart.items.filter((i) => !(i.properties && i.properties[REWARD_PROP])).reduce((sum, i) => sum + i.final_line_price, 0);
    if (qualifying >= threshold && !giftLine && !manuallyRemoved()) {
      await addLine([{ id: Number(giftVariant), quantity: 1, properties: { [REWARD_PROP]: 'gift' } }], 'ridi-reward');
      announce('ridi_reward_reached', { tier: 'gift', threshold, cart_total: qualifying });
    } else if (qualifying < threshold && giftLine) {
      await changeLine(giftLine.key, 0, 'ridi-reward');
    } else if (giftLine && giftLine.quantity > 1) {
      await changeLine(giftLine.key, 1, 'ridi-reward');
    }
  } catch (error) {
    console.warn('[ridi-cart] reward sync skipped:', error.message);
  } finally {
    rewardBusy = false;
  }
}

/* ---- Cross-sell quick add ------------------------------------------------------------------- */
async function handleCrossSellAdd(button) {
  const variant = Number(button.dataset.ridiVariant);
  if (!variant || button.disabled) return;
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  try {
    await addLine([{ id: variant, quantity: 1 }], 'ridi-crosssell');
    announce('ridi_upsell_accepted', { surface: 'cart', variant_id: variant, product_id: button.dataset.ridiProduct || null }, 'marketing');
  } catch (error) {
    button.disabled = false;
    button.removeAttribute('aria-busy');
    const note = button.closest('.ridi-xsell__item') && button.closest('.ridi-xsell__item').querySelector('.ridi-xsell__error');
    if (note) { note.textContent = error.message; note.hidden = false; }
  }
}

/* ---- Order enhancement (bump) --------------------------------------------------------------- */
let bumpBusy = false;
async function handleBumpToggle(input) {
  if (bumpBusy) return;
  bumpBusy = true;
  const variant = Number(input.dataset.ridiVariant);
  try {
    const cart = await readCart();
    const line = cart && cart.items.find((i) => i.properties && i.properties[BUMP_PROP] === '1');
    if (input.checked && !line) {
      await addLine([{ id: variant, quantity: 1, properties: { [BUMP_PROP]: '1' } }], 'ridi-bump');
      announce('ridi_upsell_accepted', { surface: 'pre-checkout', variant_id: variant }, 'marketing');
    } else if (!input.checked && line) {
      await changeLine(line.key, 0, 'ridi-bump');
      announce('ridi_upsell_rejected', { surface: 'pre-checkout', variant_id: variant });
    }
  } catch (error) {
    input.checked = !input.checked;
    console.warn('[ridi-cart] bump toggle failed:', error.message);
  } finally {
    bumpBusy = false;
  }
}

/* ---- Wiring --------------------------------------------------------------------------------- */
document.addEventListener('click', (e) => {
  const xsell = e.target.closest && e.target.closest('[data-ridi-xsell-add]');
  if (xsell) { e.preventDefault(); handleCrossSellAdd(xsell); }
});
document.addEventListener('change', (e) => {
  const bump = e.target.closest && e.target.closest('[data-ridi-bump-toggle]');
  if (bump) handleBumpToggle(bump);
});
document.addEventListener('ridi:gift-removed', () => { try { sessionStorage.setItem('ridi:gift-removed', '1'); } catch { /* ignore */ } });
document.addEventListener(ThemeEvents.cartUpdate, (e) => {
  if (e.detail && (e.detail.source === 'ridi-reward')) return;
  window.setTimeout(syncRewardGift, 150);
  const shown = document.querySelector('[data-ridi-xsell]');
  if (shown && !shown.dataset.ridiSeen) { shown.dataset.ridiSeen = '1'; announce('ridi_upsell_shown', { surface: 'cart', items: shown.querySelectorAll('[data-ridi-xsell-add]').length }); }
});
document.addEventListener('dialog:open', (e) => {
  if (e.target && e.target.matches && e.target.matches('cart-drawer-component')) {
    const shown = document.querySelector('[data-ridi-xsell]');
    if (shown && !shown.dataset.ridiSeen) { shown.dataset.ridiSeen = '1'; announce('ridi_upsell_shown', { surface: 'cart', items: shown.querySelectorAll('[data-ridi-xsell-add]').length }); }
    const bump = document.querySelector('[data-ridi-bump]');
    if (bump && !bump.dataset.ridiSeen) { bump.dataset.ridiSeen = '1'; announce('ridi_upsell_shown', { surface: 'pre-checkout' }); }
  }
}, true);
window.setTimeout(syncRewardGift, 400);
